//Author: Elli Scharlin
//Copyright (c) 2022, Audigent
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import axios from 'axios';
import Reaptcha from 'reaptcha';
import { validateEmail } from './../../utils/validator';
import { MdClose } from 'react-icons/md';
import TextInput from 'components/inputs/TextInput';
import Dropdown from 'components/inputs/dropdown';
import GoldenButton from 'components/buttons/GoldenButton';
import './style.css';

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        maxHeight: '90%',
    },
};
const Header = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`;
const Content = styled.div`
    margin-top: 20px;
    margin-left: 44px;
    margin-right: 44px;

    @media screen and (max-width: 767px) {
        margin-top: 4px;
        margin-left: 4px;
        margin-right: 4px;
    }
`;
const Heading = styled.div`
    color: #19171a;
    font-size: 36px;
    line-height: 40px;
    font-family: 'Renogare-Regular';
    letter-spacing: -1.5px;
    text-align: left;
    @media screen and (max-width: 767px) {
        font-size: 24px;
        line-height: 28px;
        letter-spacing: -0.92px;
        text-align: left;
    }
`;
const Desc = styled.div`
    margin-top: 13px;
    color: rgba(25, 23, 26, 0.9);
    font-family: 'Neuzeit Grotesk';
    font-size: 17px;
    line-height: 24px;
    text-align: left;
    @media screen and (max-width: 767px) {
        font-size: 14px;
        line-height: 20px;
    }
`;
const Form = styled.div`
    margin-top: 24px;
    margin-bottom: 25px;
    @media screen and (max-width: 767px) {
        margin-top: 18px;
        margin-bottom: 12px;
    }
`;
const FormFooter = styled.div`
    display: flex;
    justify-content: space-between;
`;
const Container = styled.div`
    font-family: 'Neuzeit Grotesk';
    line-height: 24px;
    font-size: 17px;
`;
const CheckboxGroup = styled.div`
    font-family: 'Neuzeit Grotesk';
    display: flex;
    justify-content: space-between;
    height: 50px;
    overflow: auto;
`;

const CheckboxItem = styled.span`
    white-space: no-wrap;
    justify-content: space-between;
`;
Modal.setAppElement('#___gatsby');

const inventoryTypes = ['Web', 'In-App', 'Both'];

export default function RequestDealModal({ isOpen, closeModal, dealName }) {
    const [isVerified, setIsVerified] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [dsp, setDsp] = useState('');
    const [seatId, setSeatId] = useState('');
    const [inventoryType, setInventoryType] = useState(inventoryTypes[0]);

    const [dspError, setDspError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [nameError, setNameError] = useState(false);
    const [seatIdError, setSeatIdError] = useState(false);
    const [messageSent, setMessageSent] = useState(false);

    const [displaySelect, setDisplaySelect] = useState(false);
    const [videoSelect, setVideoSelect] = useState(false);
    const [nativeSelect, setNativeSelect] = useState(false);

    /** This useEffect acts as a validity test for the deal modal. */
    useEffect(() => {
        if (!!dsp) setDspError(false);
        if (!!name) setNameError(false);
        if (!!email) setEmailError(false);
        if (!!seatId) setSeatIdError(false);
        return () => {};
    }, [dsp, email, seatId, name]);

    /** This is a captcha verification. */
    const verifyCaptcha = () => {
        setIsVerified(true);
    };

    /** The following three are boolean flags for display, video and native
     * as formats (by means of checkboxes in the form) to be used in
     * the final email that gets sent to the sales team. */
    const handleDisplaySelected = () => {
        setDisplaySelect(!displaySelect);
    };
    const handleNativeSelected = () => {
        setNativeSelect(!nativeSelect);
    };
    const handleVideoSelected = () => {
        setVideoSelect(!videoSelect);
    };

    /** When the deal is ready to be sent, this is another check that runs before sending
     * the deal through. */
    const onSend = () => {
        if (seatId.length === 0) {
            setSeatIdError(true);
            return;
        }
        if (email.length === 0 || !validateEmail(email)) {
            setEmailError(true);
            return;
        }
        if (dsp.length === 0) {
            setDspError(true);
            return;
        }
        if (name.length === 0) {
            setNameError(true);
            return;
        }

        if (isVerified) {
            const formattedMessage = `\nI am interested in the segment: ${dealName}\n\nUsing the DSP: ${dsp},\nOur seatID is: ${seatId}\nWe want the format(s): ${
                displaySelect ? 'display, ' : ''
            }${nativeSelect ? 'native, ' : ''}${
                videoSelect ? 'video' : ''
            } \nAnd an inventory of: ${inventoryType} \n\nMy name is: ${name}\nMy email address is:\n${email}`;
            let data = {
                name: name,
                email: email,
                message: formattedMessage,
                type: 3,
            };
            axios({
                method: 'post',
                url:
                    'https://jmwtq703p5.execute-api.us-west-2.amazonaws.com/default',
                data: data,
            })
                .then(() => {
                    console.log('success');
                    setMessageSent(true);
                    setTimeout(onClose, 3000);
                })
                .catch(err => {
                    console.log('error', err);
                });
        }
    };

    useEffect(() => {
        return () => {};
    }, [isOpen]);

    /** Validity check reset for modal being closed (so that previous deal data does not persist). */
    const onClose = () => {
        closeModal();
        setEmail('');
        setDsp('');
        setSeatId('');
        setEmailError(false);
        setNameError(false);
        setMessageSent(false);
    };

    return (
        <Modal
            isOpen={isOpen}
            // onAfterOpen={afterOpenModal}
            onRequestClose={() => onClose()}
            style={customStyles}
            contentLabel="Example Modal"
        >
            <Header>
                <MdClose
                    size={24}
                    color="rgba(0,0,0,0.5)"
                    onClick={() => onClose()}
                    style={{ cursor: 'pointer' }}
                />
            </Header>
            {messageSent && (
                <Content>
                    <Heading>Thank you!</Heading>
                    <Desc>Our team will be in touch soon.</Desc>
                </Content>
            )}
            {!messageSent && (
                <Content>
                    <Heading>Request Deal ID</Heading>
                    <Desc>
                        Please enter the information below and we will deliver
                        your Comscore CognitivePMP for activation.
                    </Desc>
                    <Form>
                        <TextInput
                            title="Deal Name"
                            value={dealName}
                            theme="white"
                        />
                        <TextInput
                            title="Name"
                            value={name}
                            status={nameError ? 'error' : ''}
                            onTextChange={text => setName(text)}
                            theme="white"
                        />
                        <TextInput
                            title="Email Address"
                            value={email}
                            theme="white"
                            status={emailError ? 'error' : ''}
                            onTextChange={text => setEmail(text)}
                        />
                        <Dropdown
                            title="Inventory Type..."
                            list={inventoryTypes}
                            current={0}
                            onChooseItem={e => {
                                setInventoryType(e);
                            }}
                            isLight={true}
                        />
                        <Container>
                            Ad Format Types...
                            <CheckboxGroup>
                                <CheckboxItem>
                                    <input
                                        id="Display"
                                        type="checkbox"
                                        value="Display"
                                        name="Display"
                                        onChange={handleDisplaySelected}
                                        checked={displaySelect}
                                    />
                                    <label htmlFor="Display">Display</label>
                                </CheckboxItem>
                                <CheckboxItem>
                                    <input
                                        label="Native"
                                        id="Native"
                                        type="checkbox"
                                        value="Native"
                                        name="Native"
                                        onChange={handleNativeSelected}
                                        checked={nativeSelect}
                                    />
                                    <label htmlFor="Native">Native</label>
                                </CheckboxItem>
                                <CheckboxItem>
                                    <input
                                        id="Video"
                                        label="Video"
                                        type="checkbox"
                                        value="Video"
                                        name="Video"
                                        onChange={handleVideoSelected}
                                        checked={videoSelect}
                                    />
                                    <label htmlFor="Video">Video</label>
                                </CheckboxItem>
                                {/*</CheckboxList>*/}
                            </CheckboxGroup>
                        </Container>
                        <TextInput
                            title="DSP Name"
                            value={dsp}
                            theme="white"
                            status={dspError ? 'error' : ''}
                            onTextChange={text => setDsp(text)}
                        />
                        <TextInput
                            title="Seat ID"
                            value={seatId}
                            theme="white"
                            status={seatIdError ? 'error' : ''}
                            onTextChange={text => setSeatId(text)}
                        />
                        <FormFooter>
                            <Reaptcha
                                sitekey="6Lfk78AZAAAAAEju21TZXvSK4P2O44_gyaSlwqhc"
                                onVerify={verifyCaptcha}
                            />
                            <GoldenButton onClick={() => onSend()}>
                                REQUEST DEAL
                            </GoldenButton>
                        </FormFooter>
                    </Form>
                </Content>
            )}
        </Modal>
    );
}
